import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { useAutomationCard } from "@/pages/Automation/hooks/useAutomationCard";
import { AutomationDto } from "@/shared/dtos/automation-dto";
import { formatTimestampWithoutTime } from "@/utils/format-utils";
import "./AutomationCard.scss";

interface AutomationCardProps {
	automation: AutomationDto;
	onPlay: () => Promise<void>;
	onPause: () => Promise<void>;
	onStop: () => Promise<void>;
	onResume: () => Promise<void>;
	onViewDetail?: () => void;
}

export default function AutomationCard({
	automation,
	onPlay,
	onPause,
	onStop,
	onResume,
	onViewDetail,
}: Readonly<AutomationCardProps>) {
	const { t } = useLanguage();
	const {
		statusCode,
		typeIcon,
		typeLabelKey,
		statusDotClass,
		isPulsing,
		translatedStatus,
		isError,
		errorMessage,
		canPlay,
		canPause,
		canStop,
		canResume,
		hasActions,
		processingAction,
		handlePlay,
		handlePause,
		handleStop,
		handleResume,
	} = useAutomationCard(automation, onPlay, onPause, onStop, onResume);

	return (
		<div
			className="automation-card"
			onClick={onViewDetail}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") e.stopPropagation();
			}}
		>
			<div className="automation-card-header">
				<i
					className={`mdi automation-card-icon ${typeIcon}`}
					title={t(typeLabelKey)}
				/>
				<div className="automation-card-title" title={automation.sessionName}>
					{automation.sessionName}
				</div>
			</div>

			<div
				className={`automation-description${!automation.description ? " automation-description--empty" : ""}`}
				title={automation.description || t("automation.noDescription")}
			>
				{automation.description || t("automation.noDescription")}
			</div>

			<div className="automation-card-status-row">
				<span className="automation-card-type">{t(typeLabelKey)}</span>
				{statusCode && (
					<>
						<span className="automation-separator"> • </span>
						<div
							className={`automation-status-chip ${statusCode.toLowerCase()}`}
						>
							{translatedStatus}
						</div>
						<div className="automation-status-indicator">
							<span
								className={`status-dot ${statusDotClass}${isPulsing ? " pulse" : ""}`}
							/>
						</div>
						{hasActions && (
							<div
								className="automation-card-actions"
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}
							>
								{canPlay && (
									<button
										type="button"
										className="automation-action-btn"
										title={t("automation.actions.play.tooltip")}
										disabled={!!processingAction}
										onClick={handlePlay}
									>
										<i className="mdi mdi-play" />
									</button>
								)}
								{canPause && (
									<button
										type="button"
										className="automation-action-btn"
										title={t("automation.actions.pause.tooltip")}
										disabled={!!processingAction}
										onClick={handlePause}
									>
										<i className="mdi mdi-pause" />
									</button>
								)}
								{canResume && (
									<button
										type="button"
										className="automation-action-btn"
										title={t("automation.actions.resume.tooltip")}
										disabled={!!processingAction}
										onClick={handleResume}
									>
										<i className="mdi mdi-replay" />
									</button>
								)}
								{canStop && (
									<button
										type="button"
										className="automation-action-btn"
										title={t("automation.actions.stop.tooltip")}
										disabled={!!processingAction}
										onClick={handleStop}
									>
										<i className="mdi mdi-stop" />
									</button>
								)}
							</div>
						)}
					</>
				)}
			</div>

			{isError && (
				<span className="automation-error-message">{errorMessage}</span>
			)}

			<div className="automation-card-footer">
				{automation.organizationName && (
					<span>{automation.organizationName}</span>
				)}
				{automation.organizationName &&
					typeof automation.createdDate === "number" && <span>•</span>}
				{typeof automation.createdDate === "number" && (
					<span>
						{formatTimestampWithoutTime(automation.createdDate * 1000)}
					</span>
				)}
			</div>

			{processingAction && (
				<div className="automation-processing">
					<span className="dot-flashing" />
					<span>{processingAction}</span>
				</div>
			)}
		</div>
	);
}
